/**
 * Admin Service
 * Handles all admin-related API calls for platform management
 */

import { api, ApiResponse } from './api';
import { User } from './authService';

export interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
  images: string[];
  farmer: User | string;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  updatedAt: string;
}

export interface Order {
  _id: string;
  buyer: User | string;
  farmer: User | string;
  products: {
    product: Product | string;
    quantity: number;
    price: number;
  }[];
  totalAmount: number;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  deliveryAddress: string;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardStats {
  totalUsers: number;
  totalFarmers: number;
  totalBuyers: number;
  totalProducts: number;
  totalOrders: number;
  totalRevenue: number;
  activeOrders: number;
  pendingProducts: number;
}

class AdminService {
  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return api.get<DashboardStats>('/admin/stats');
  }

  // ==================== USER MANAGEMENT ====================

  /**
   * Get all users
   */
  async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
  }): Promise<ApiResponse<{ users: User[]; total: number }>> {
    const queryParams = new URLSearchParams(params as any).toString();
    return api.get(`/admin/users?${queryParams}`);
  }

  /**
   * Get single user
   */
  async getUser(userId: string): Promise<ApiResponse<User>> {
    return api.get(`/admin/users/${userId}`);
  }

  /**
   * Create new user
   */
  async createUser(userData: Partial<User>): Promise<ApiResponse<User>> {
    return api.post('/admin/users', userData);
  }

  /**
   * Update user
   */
  async updateUser(userId: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    return api.put(`/admin/users/${userId}`, userData);
  }

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<ApiResponse> {
    return api.delete(`/admin/users/${userId}`);
  }

  /**
   * Toggle user status (active/inactive)
   */
  async toggleUserStatus(userId: string): Promise<ApiResponse<User>> {
    return api.put(`/admin/users/${userId}/toggle-status`, {});
  }

  // ==================== PRODUCT MANAGEMENT ====================

  /**
   * Get all products
   */
  async getProducts(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<{ products: Product[]; total: number }>> {
    const queryParams = new URLSearchParams(params as any).toString();
    return api.get(`/admin/products?${queryParams}`);
  }

  /**
   * Get single product
   */
  async getProduct(productId: string): Promise<ApiResponse<Product>> {
    return api.get(`/admin/products/${productId}`);
  }

  /**
   * Update product
   */
  async updateProduct(productId: string, productData: Partial<Product>): Promise<ApiResponse<Product>> {
    return api.put(`/admin/products/${productId}`, productData);
  }

  /**
   * Delete product
   */
  async deleteProduct(productId: string): Promise<ApiResponse> {
    return api.delete(`/admin/products/${productId}`);
  }

  /**
   * Approve product
   */
  async approveProduct(productId: string): Promise<ApiResponse<Product>> {
    return api.put(`/admin/products/${productId}/approve`, {});
  }

  /**
   * Reject product
   */
  async rejectProduct(productId: string, reason?: string): Promise<ApiResponse<Product>> {
    return api.put(`/admin/products/${productId}/reject`, { reason });
  }

  // ==================== ORDER MANAGEMENT ====================

  /**
   * Get all orders
   */
  async getOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<{ orders: Order[]; total: number }>> {
    const queryParams = new URLSearchParams(params as any).toString();
    return api.get(`/admin/orders?${queryParams}`);
  }

  /**
   * Get single order
   */
  async getOrder(orderId: string): Promise<ApiResponse<Order>> {
    return api.get(`/admin/orders/${orderId}`);
  }

  /**
   * Update order status
   */
  async updateOrderStatus(orderId: string, status: Order['status']): Promise<ApiResponse<Order>> {
    return api.put(`/admin/orders/${orderId}/status`, { status });
  }

  /**
   * Cancel order
   */
  async cancelOrder(orderId: string, reason?: string): Promise<ApiResponse<Order>> {
    return api.put(`/admin/orders/${orderId}/cancel`, { reason });
  }

  // ==================== ANALYTICS ====================

  /**
   * Get revenue analytics
   */
  async getRevenueAnalytics(period: 'week' | 'month' | 'year'): Promise<ApiResponse<any>> {
    return api.get(`/admin/analytics/revenue?period=${period}`);
  }

  /**
   * Get user growth analytics
   */
  async getUserGrowthAnalytics(period: 'week' | 'month' | 'year'): Promise<ApiResponse<any>> {
    return api.get(`/admin/analytics/users?period=${period}`);
  }

  /**
   * Get sales analytics
   */
  async getSalesAnalytics(period: 'week' | 'month' | 'year'): Promise<ApiResponse<any>> {
    return api.get(`/admin/analytics/sales?period=${period}`);
  }

  // ==================== SYSTEM SETTINGS ====================

  /**
   * Get system settings
   */
  async getSettings(): Promise<ApiResponse<any>> {
    return api.get('/admin/settings');
  }

  /**
   * Update system settings
   */
  async updateSettings(settings: any): Promise<ApiResponse<any>> {
    return api.put('/admin/settings', settings);
  }
}

export const adminService = new AdminService();

